import { useState, useMemo, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, TextField, IconButton, Chip, Stack, Paper, Divider,
  List, ListItemButton, ListItemText, InputAdornment,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useApp } from '../contexts';
import { searchQuestions } from '../utils';
import QuestionPreviewDialog, { type PreviewItem } from '../components/QuestionPreviewDialog';

const TYPE_LABELS: Record<string, string> = { single: '单选', multiple: '多选', judge: '判断' };

export default function Search() {
  const { state } = useApp();
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState<PreviewItem[]>([]);
  const [searched, setSearched] = useState(false);
  const [previewItem, setPreviewItem] = useState<PreviewItem | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(0 as unknown as ReturnType<typeof setTimeout>);

  const handleChange = useCallback((value: string) => {
    setKeyword(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!value.trim()) return;
      setResults(searchQuestions(state.banks, value.trim()));
      setSearched(true);
    }, 200);
  }, [state.banks]);

  const handleSearchImmediate = useCallback(() => {
    if (!keyword.trim()) return;
    clearTimeout(debounceRef.current);
    setResults(searchQuestions(state.banks, keyword.trim()));
    setSearched(true);
  }, [keyword, state.banks]);

  const totalCount = useMemo(() => state.banks.reduce((s, b) => s + b.hqb.questions.length, 0), [state.banks]);

  const searchAdornment = useMemo(() => (
    <InputAdornment position="end">
      <IconButton size="small" onClick={handleSearchImmediate} edge="end">
        <SearchRoundedIcon fontSize="small" />
      </IconButton>
    </InputAdornment>
  ), [handleSearchImmediate]);

  return (
    <Box sx={{ p: 2, maxWidth: 720, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>搜索题目</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        全题库搜索 · {totalCount} 道题
      </Typography>

      <TextField
        fullWidth placeholder="输入关键词搜索..."
        size="medium" value={keyword}
        onChange={e => handleChange(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && handleSearchImmediate()}
        sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
        slotProps={{ input: { endAdornment: searchAdornment } }}
        autoComplete="off"
      />

      {searched && results.length > 0 && (
        <Card elevation={0} sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 4 }}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <List disablePadding>
              {results.map((item, idx) => (
                <Box key={`${item.bank.id}-${item.question.id}`}>
                  {idx > 0 && <Divider />}
                  <ListItemButton sx={{ py: 2, px: 2.5 }} onClick={() => setPreviewItem(item)}>
                    <ListItemText
                      primary={item.question.content}
                      secondary={
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                          <Chip label={TYPE_LABELS[item.question.type]} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                          <Chip label={item.bank.name} size="small" color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                        </Stack>
                      }
                      slotProps={{ primary: { variant: 'body2' } }}
                    />
                  </ListItemButton>
                </Box>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {searched && results.length === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 4, bgcolor: 'background.paper' }}>
          <Typography color="text.secondary">未找到匹配的题目</Typography>
        </Paper>
      )}

      <QuestionPreviewDialog item={previewItem} onClose={() => setPreviewItem(null)} />
    </Box>
  );
}
